import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAtomValue } from 'jotai';
import {
  countStyleMasterPriceItems,
  StyleMasterPriceSnapshot,
  type StyleMasterPriceTarget,
} from '@/components/Approval/@components/StyleMasterPrice';
import usePartialReject, {
  getPartialRejectErrorMessage,
} from '@/hooks/approval/usePartialReject';
import useApprovalView from '@/hooks/approval/useApprovalView';
import { useDialog, useSnackbar } from '@/hooks/global-tools';
import { MyInfoState } from '@/recoil/atom';
import { parseStyleMasterPriceContent } from '@/utils/approval/styleMasterPrice';
import { isCurrentApproverMeOf } from '../../actionVisibility';
import ReasonInputModal from '../../ReasonInputModal';
import type { ContentExtraProps } from '../../types';
import { ContentSlotRoot } from '../styles';

const REASON_MAX_LENGTH = 300;

/**
 * 128 GERP STYLE MASTER 가격 변경 — 저장 표 HTML 그대로(레거시 문서 포함). GERP 재조회 안 함.
 * 부분반려 버튼 노출: 문서 진행중 + 지금 내 결재 차례 + 건 2개 이상(레거시 조건)
 */
const StyleMasterPriceSlot = ({ data }: ContentExtraProps) => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useSnackbar();
  const dialog = useDialog();
  const { data: view } = useApprovalView(data.documentId);
  const myInfo = useAtomValue(MyInfoState);
  const { partialReject, submitting } = usePartialReject(data.documentId);
  const [target, setTarget] = useState<StyleMasterPriceTarget | null>(null);

  const content = parseStyleMasterPriceContent(data.attributes);
  if (!content) return null;

  const canPartialReject =
    !!view &&
    view.status === 'P' &&
    isCurrentApproverMeOf(
      view.approvers,
      view.currentApproverOrder,
      myInfo?.passportId ?? '',
      view.status,
    ) &&
    countStyleMasterPriceItems(content) >= 2;

  const submit = async (item: StyleMasterPriceTarget, reason: string) => {
    try {
      await partialReject(item.itemId, reason);
      setTarget(null);
      showSuccess({ message: t('부분 반려가 완료 되었습니다.') });
    } catch (error) {
      showError({ message: t(getPartialRejectErrorMessage(error)) });
    }
  };

  // 레거시: 사유 입력 → 확인창 → 처리
  const handleSubmit = (reason: string) => {
    if (!target) return;
    const item = target;
    dialog.show({
      message: t('정말로 부분 반려 하시겠습니까?'),
      confirmButtonText: t('확인'),
      cancelButtonText: t('취소'),
      onConfirm: () => submit(item, reason),
    });
  };

  return (
    <ContentSlotRoot>
      <StyleMasterPriceSnapshot
        content={content}
        actionTitle={t('부분반려')}
        onPartialReject={canPartialReject ? setTarget : undefined}
      />
      <ReasonInputModal
        open={!!target}
        onClose={() => setTarget(null)}
        onSubmit={handleSubmit}
        submitting={submitting}
        title={t('{{styleNo}} 반려', { styleNo: target?.styleNo ?? '' })}
        description={t('이 품번만 결재에서 제외됩니다.')}
        reasonLabel={t('사유입력')}
        reasonPlaceholder={t('사유를 입력해주세요.')}
        reasonMaxLength={REASON_MAX_LENGTH}
      />
    </ContentSlotRoot>
  );
};

export default StyleMasterPriceSlot;
